import React from 'react';
import { Plus } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import type { ProjectRole } from '../types';

interface RolesTableProps {
  roles: ProjectRole[];
  canCreate?: boolean;
  onCreate: () => void;
  onView?: (role: ProjectRole) => void;
  onEdit: (role: ProjectRole) => void;
  onDelete: (role: ProjectRole) => void;
}

export const RolesTable: React.FC<RolesTableProps> = ({
  roles,
  canCreate = true,
  onCreate,
  onView,
  onEdit,
  onDelete,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button size="sm" onClick={onCreate} disabled={!canCreate}>
          <Plus className="mr-1 h-4 w-4" />
          Add Role
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead>Flags</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead>Members</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  No roles configured for this project yet.
                </TableCell>
              </TableRow>
            ) : (
              roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-foreground">{role.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Updated {new Date(role.updated_at).toLocaleString()}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {role.is_admin && <Badge variant="destructive">Admin</Badge>}
                      {role.is_default && <Badge variant="secondary">Default</Badge>}
                      {!role.is_admin && !role.is_default && (
                        <span className="text-sm text-muted-foreground">Custom</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-foreground">{role.permissions.length}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-foreground">{role.member_count}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {onView && (
                        <Button size="sm" variant="ghost" onClick={() => onView(role)}>
                          View
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => onEdit(role)}>
                        Edit
                      </Button>
                      {!(role.is_admin || role.is_default) && (
                        <Button size="sm" variant="destructive" onClick={() => onDelete(role)}>
                          Delete
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
